import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import {
  DEFAULT_VALIDITY_MONTHS,
  addTemperatureValidityDaysToIsoDate,
  calculateTemperatureResults,
  configureTemperatureSessionStandards,
  createTemperatureSession,
  getTemperatureSession,
  getTemperatureStandardConfiguration,
  getTodayDailyEnvironmentLog,
  listTemperatureBlocks,
  listTemperaturePoints,
  listTemperatureResults,
  listTemperatureSessionsByInstrument,
  listTemperatureStandardOptions,
  saveTemperaturePoints,
  temperaturePointsToRows,
  updateTemperatureSession,
  upsertTodayDailyEnvironmentLog,
  validateTemperatureStandardConfiguration,
} from '../../utils/api-temperature-calibration';
import {
  TEMPERATURE_CONTACT_TEMPLATE,
  TEMPERATURE_READING_FIELDS,
  computeTemperaturePointMetrics,
  mergeTemperatureFamilyData,
  resolveTemperatureTemplate,
} from '../../utils/temperature-calibration-profile';

const VALIDITY_OPTIONS = [
  { value: 6, label: '6 meses' },
  { value: 12, label: '1 año' },
  { value: 24, label: '2 años' },
];
const EDITABLE_FIELDS = ['setpoint', ...TEMPERATURE_READING_FIELDS];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function finiteOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function average(values = []) {
  const finite = values.map(finiteOrNull).filter((value) => value !== null);
  if (!finite.length) return null;
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
}

function fmt(value, decimals = 4) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(decimals) : '—';
}

function formatDate(value) {
  const date = String(value || '').slice(0, 10);
  if (!date) return '—';
  const [year, month, day] = date.split('-');
  return year && month && day ? `${day}/${month}/${year}` : '—';
}

function normalizeDailyEnvironment(value = {}) {
  return {
    pressure_hpa: Number(value?.pressure_hpa ?? 0),
    temp_c: Number(value?.temp_c ?? 0),
    humidity_rh: Number(value?.humidity_rh ?? 0),
    pressure_unc_hpa: Number(value?.pressure_unc_hpa ?? 1),
    temp_unc_c: Number(value?.temp_unc_c ?? 1),
    humidity_unc_rh: Number(value?.humidity_unc_rh ?? 3),
    notes: value?.notes || '',
  };
}

function emptyRows(count = TEMPERATURE_CONTACT_TEMPLATE.pointCount) {
  return Array.from({ length: count }, (_, index) => ({
    point_no: index + 1,
    setpoint: '',
    s1_asc_pattern: '',
    s1_asc_instrument: '',
    s1_desc_pattern: '',
    s1_desc_instrument: '',
    s2_asc_pattern: '',
    s2_asc_instrument: '',
    s2_desc_pattern: '',
    s2_desc_instrument: '',
  }));
}

function sessionWithAverages(session) {
  if (!session) return session;
  const familyData = mergeTemperatureFamilyData(session.family_data, session.template_code);
  const readings = familyData.environment_readings || [];
  return {
    ...session,
    family_data: familyData,
    ambient_temp_avg: average(readings.map((item) => item.temp_c)) ?? finiteOrNull(session.ambient_temp_avg),
    ambient_pressure_avg: average(readings.map((item) => item.pressure_hpa)) ?? finiteOrNull(session.ambient_pressure_avg),
    ambient_humidity_avg: average(readings.map((item) => item.humidity_rh)) ?? finiteOrNull(session.ambient_humidity_avg),
  };
}

export default function TemperatureCalibrationPage() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialSessionId = searchParams.get('sessionId') || '';
  const serviceOrderCode = searchParams.get('serviceOrderCode') || '';
  const routeTemplateCode = location.pathname.includes('/temperature/infrared/')
    ? 'TEMP-IR:pyrometer:v1'
    : location.pathname.includes('/temperature/sensor/')
      ? 'TEMP-SENSOR:rtd_thermocouple:v1'
      : location.pathname.includes('/temperature/chamber/')
        ? 'TEMP-CHAMBER:oven_chamber:v1'
        : TEMPERATURE_CONTACT_TEMPLATE.code;
  const requestedTemplateCode = searchParams.get('templateCode') || routeTemplateCode;
  const inputRefs = useRef({});

  const [instrument, setInstrument] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [session, setSession] = useState(null);
  const [block, setBlock] = useState(null);
  const [rows, setRows] = useState(emptyRows());
  const [results, setResults] = useState([]);
  const [dailyEnvironment, setDailyEnvironment] = useState(normalizeDailyEnvironment());
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [running, setRunning] = useState(false);
  const [savingDailyEnvironment, setSavingDailyEnvironment] = useState(false);
  const [standardOptions, setStandardOptions] = useState({
    source: [],
    reference: [],
    environment: [],
  });
  const [selectedStandards, setSelectedStandards] = useState({
    source: '',
    reference: '',
    environment: '',
  });
  const [standardValidation, setStandardValidation] = useState({
    valid: false,
    issues: ['Falta configurar los instrumentos patrones.'],
    configuration: {},
  });
  const [loadingStandards, setLoadingStandards] = useState(false);
  const [configuringStandards, setConfiguringStandards] = useState(false);
  const [notice, setNotice] = useState(null);

  const activeTemplate = useMemo(
    () => resolveTemperatureTemplate(session?.template_code || requestedTemplateCode),
    [session?.template_code, requestedTemplateCode],
  );

  const averagedSession = useMemo(() => sessionWithAverages(session), [session]);
  const familyData = useMemo(
    () => mergeTemperatureFamilyData(averagedSession?.family_data, activeTemplate.code),
    [averagedSession?.family_data, activeTemplate.code],
  );
  const validityMonths = Number(averagedSession?.validity_months || DEFAULT_VALIDITY_MONTHS);
  const calibrationDate = averagedSession?.calibration_date || todayIso();
  const validUntil = addTemperatureValidityDaysToIsoDate(calibrationDate, validityMonths);
  const liveResults = useMemo(
    () => rows
      .map((row) => {
        const metrics = computeTemperaturePointMetrics(row, averagedSession || {});
        return metrics && !metrics.traceability_issue ? { point_no: row.point_no, ...metrics } : null;
      })
      .filter(Boolean),
    [rows, averagedSession],
  );
  const liveTraceabilityIssue = useMemo(
    () => rows
      .map((row) => computeTemperaturePointMetrics(row, averagedSession || {}))
      .find((metrics) => metrics?.traceability_issue)?.traceability_issue || null,
    [rows, averagedSession],
  );

  async function loadInstrument() {
    const { data, error } = await supabase
      .from('instruments')
      .select('id, tag, lab_id, brand, model, serial, type, unit, range_min, range_max, resolution, tolerance, client:client_id (name)')
      .eq('id', instrumentId)
      .single();
    if (error) throw error;
    setInstrument(data);
  }

  async function loadSessions() {
    const list = await listTemperatureSessionsByInstrument(
      instrumentId,
      initialSessionId ? null : requestedTemplateCode,
    );
    setSessions(list);
    if (!sessionId && !initialSessionId && list.length) setSessionId(list[0].id);
  }

  async function loadSessionData(id) {
    if (!id) {
      setSession(null);
      setBlock(null);
      setRows(emptyRows());
      setResults([]);
      setSelectedStandards({ source: '', reference: '', environment: '' });
      setStandardValidation({
        valid: false,
        issues: ['Falta configurar los instrumentos patrones.'],
        configuration: {},
      });
      return;
    }
    const [loadedSession, blocks, points, loadedResults, validation, configuration] = await Promise.all([
      getTemperatureSession(id),
      listTemperatureBlocks(id),
      listTemperaturePoints(id),
      listTemperatureResults(id),
      validateTemperatureStandardConfiguration(id),
      getTemperatureStandardConfiguration(id),
    ]);
    const loadedBlock = blocks.find((item) => item.block_key === 'temperature_contact') || blocks[0] || null;
    const normalizedSession = {
      ...loadedSession,
      family_data: mergeTemperatureFamilyData(loadedSession.family_data, loadedSession.template_code),
    };
    setSession(normalizedSession);
    setBlock(loadedBlock);
    setRows(loadedBlock ? temperaturePointsToRows(loadedBlock, points, normalizedSession) : emptyRows());
    setResults(loadedResults || []);
    const effectiveConfiguration = validation?.configuration || configuration || {};
    setStandardValidation({
      valid: Boolean(validation?.valid),
      issues: validation?.issues || [],
      configuration: effectiveConfiguration,
    });
    setSelectedStandards({
      source: effectiveConfiguration?.source?.capability?.id || '',
      reference: effectiveConfiguration?.reference?.capability?.id || '',
      environment: effectiveConfiguration?.environment?.capability?.id || '',
    });
  }

  async function loadStandardOptionsFor(currentSession) {
    if (!currentSession) return;
    try {
      setLoadingStandards(true);
      const params = {
        onDate: currentSession.calibration_date || todayIso(),
        rangeMin: currentSession.instrument_range_min,
        rangeMax: currentSession.instrument_range_max,
      };
      const [source, reference, environment] = await Promise.all([
        listTemperatureStandardOptions({ ...params, usageRole: 'source' }),
        listTemperatureStandardOptions({ ...params, usageRole: 'reference' }),
        listTemperatureStandardOptions({ ...params, usageRole: 'environment' }),
      ]);
      setStandardOptions({ source, reference, environment });
    } catch (error) {
      console.error('[Temperature] standard options', error);
      setStandardOptions({ source: [], reference: [], environment: [] });
      setNotice({
        type: 'error',
        text: `No se pudieron consultar los patrones aprobados: ${error?.message || error}`,
      });
    } finally {
      setLoadingStandards(false);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadInstrument(),
          loadSessions(),
          getTodayDailyEnvironmentLog()
            .then((value) => { if (alive) setDailyEnvironment(normalizeDailyEnvironment(value)); })
            .catch(() => {}),
        ]);
      } catch (error) {
        console.error('[Temperature] load', error);
        setNotice({ type: 'error', text: 'No se pudo cargar el módulo de temperatura.' });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [instrumentId]);

  useEffect(() => {
    loadSessionData(sessionId).catch((error) => {
      console.error('[Temperature] session', error);
      setNotice({ type: 'error', text: 'No se pudo cargar la sesión seleccionada.' });
    });
  }, [sessionId]);

  useEffect(() => {
    if (!session) return;
    loadStandardOptionsFor(session);
  }, [
    session?.id,
    session?.calibration_date,
    session?.instrument_range_min,
    session?.instrument_range_max,
  ]);

  function updateSessionField(key, value) {
    setSession((current) => ({ ...current, [key]: value }));
    if (['calibration_date', 'instrument_range_min', 'instrument_range_max'].includes(key)) {
      setStandardValidation((current) => ({
        ...current,
        valid: false,
        issues: ['Cambió la fecha o el rango. Volvé a validar la configuración patrón.'],
      }));
    }
  }

  function updateEnvironmentReading(index, key, value) {
    setSession((current) => {
      const nextFamilyData = mergeTemperatureFamilyData(current?.family_data, current?.template_code);
      const environmentReadings = nextFamilyData.environment_readings.map((reading, readingIndex) => (
        readingIndex === index ? { ...reading, [key]: value } : reading
      ));
      return {
        ...current,
        family_data: { ...nextFamilyData, environment_readings: environmentReadings },
      };
    });
  }

  function updateFamilySection(section, key, value) {
    setSession((current) => {
      const nextFamilyData = mergeTemperatureFamilyData(current?.family_data, current?.template_code);
      return {
        ...current,
        family_data: {
          ...nextFamilyData,
          [section]: { ...(nextFamilyData[section] || {}), [key]: value },
        },
      };
    });
  }

  function updateBudgetField(key, value) {
    updateFamilySection('standard_budget', key, value);
  }

  function updateRow(index, key, value) {
    setRows((current) => current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [key]: value } : row
    )));
  }

  function focusNextCell(rowIndex, field) {
    const fieldIndex = EDITABLE_FIELDS.indexOf(field);
    let nextRow = rowIndex + 1;
    let nextField = field;
    if (nextRow >= rows.length) {
      nextRow = 0;
      nextField = EDITABLE_FIELDS[fieldIndex + 1] || EDITABLE_FIELDS[0];
    }
    inputRefs.current[`${nextRow}-${nextField}`]?.focus();
  }

  async function handleCreateSession() {
    try {
      setCreating(true);
      const created = await createTemperatureSession({
        instrumentId,
        serviceOrderCode,
        templateCode: requestedTemplateCode,
      });
      await loadSessions();
      setSessionId(created.id);
      setNotice({ type: 'success', text: 'Sesión de temperatura creada.' });
    } catch (error) {
      console.error('[Temperature] create', error);
      const migrationPending = /family_data|uncertainty_components|schema cache/i.test(String(error?.message || ''));
      setNotice({
        type: 'error',
        text: migrationPending
          ? 'La actualización de base para Temperatura todavía no está aplicada.'
          : `No se pudo crear la sesión: ${error?.message || error}`,
      });
    } finally {
      setCreating(false);
    }
  }

  async function handleSaveDailyEnvironment() {
    try {
      setSavingDailyEnvironment(true);
      const saved = await upsertTodayDailyEnvironmentLog(dailyEnvironment);
      setDailyEnvironment(normalizeDailyEnvironment(saved));
      setNotice({ type: 'success', text: 'Condiciones ambientales del día guardadas.' });
    } catch (error) {
      console.error('[Temperature] daily environment', error);
      setNotice({ type: 'error', text: 'No se pudieron guardar las condiciones del día.' });
    } finally {
      setSavingDailyEnvironment(false);
    }
  }

  function handleApplyDailyEnvironment() {
    if (!session) return;
    setSession((current) => {
      const nextFamilyData = mergeTemperatureFamilyData(current.family_data, current.template_code);
      return {
        ...current,
        ambient_temp_avg: dailyEnvironment.temp_c,
        ambient_pressure_avg: dailyEnvironment.pressure_hpa,
        ambient_humidity_avg: dailyEnvironment.humidity_rh,
        family_data: {
          ...nextFamilyData,
          environment_readings: nextFamilyData.environment_readings.map((reading) => ({
            ...reading,
            temp_c: dailyEnvironment.temp_c,
            pressure_hpa: dailyEnvironment.pressure_hpa,
            humidity_rh: dailyEnvironment.humidity_rh,
          })),
        },
      };
    });
    setNotice({ type: 'success', text: 'Condiciones aplicadas a las tres lecturas de la sesión.' });
  }

  async function persist() {
    if (!sessionId || !session || !block) throw new Error('Primero creá o seleccioná una sesión.');
    const normalized = sessionWithAverages(session);
    const months = Number(normalized.validity_months || DEFAULT_VALIDITY_MONTHS);
    await updateTemperatureSession(sessionId, {
      instrument_resolution: finiteOrNull(normalized.instrument_resolution),
      instrument_range_min: finiteOrNull(normalized.instrument_range_min),
      instrument_range_max: finiteOrNull(normalized.instrument_range_max),
      instrument_class: finiteOrNull(normalized.instrument_class),
      ambient_temp_avg: finiteOrNull(normalized.ambient_temp_avg),
      ambient_pressure_avg: finiteOrNull(normalized.ambient_pressure_avg),
      ambient_humidity_avg: finiteOrNull(normalized.ambient_humidity_avg),
      calibration_date: normalized.calibration_date || todayIso(),
      validity_months: months,
      valid_until: addTemperatureValidityDaysToIsoDate(normalized.calibration_date || todayIso(), months),
      family_data: mergeTemperatureFamilyData(normalized.family_data, normalized.template_code),
      notes: normalized.notes || '',
      status: 'in_progress',
    });
    await saveTemperaturePoints(sessionId, block, rows);
  }

  async function handleConfigureStandards() {
    try {
      setConfiguringStandards(true);
      await persist();
      const validation = await configureTemperatureSessionStandards(sessionId, {
        sourceCapabilityId: selectedStandards.source,
        referenceCapabilityId: selectedStandards.reference,
        environmentCapabilityId: selectedStandards.environment,
      });
      await loadSessionData(sessionId);
      setStandardValidation(validation);
      setNotice({
        type: 'success',
        text: 'Configuración patrón validada y congelada para esta sesión.',
      });
    } catch (error) {
      console.error('[Temperature] configure standards', error);
      setStandardValidation((current) => ({
        ...current,
        valid: false,
        issues: [error?.message || String(error)],
      }));
      setNotice({
        type: 'error',
        text: `No se pudo validar la configuración patrón: ${error?.message || error}`,
      });
    } finally {
      setConfiguringStandards(false);
    }
  }

  async function handleSaveAndCalculate() {
    try {
      setRunning(true);
      await persist();
      const calculated = await calculateTemperatureResults(sessionId);
      await loadSessionData(sessionId);
      await loadSessions();
      setNotice({
        type: calculated.length ? 'success' : 'error',
        text: calculated.length
          ? 'Guardado y cálculo completos. La sesión quedó pendiente de aprobación.'
          : 'No se calcularon resultados. Completá al menos un par patrón/instrumento.',
      });
    } catch (error) {
      console.error('[Temperature] save/calculate', error);
      setNotice({ type: 'error', text: `No se pudo completar el cálculo: ${error?.message || error}` });
    } finally {
      setRunning(false);
    }
  }

  async function handleSaveDraft() {
    try {
      setSavingDraft(true);
      await persist();
      await loadSessionData(sessionId);
      await loadSessions();
      setNotice({
        type: 'success',
        text: 'Borrador guardado. Podés continuar la toma de datos más tarde.',
      });
    } catch (error) {
      console.error('[Temperature] save draft', error);
      setNotice({ type: 'error', text: `No se pudo guardar el borrador: ${error?.message || error}` });
    } finally {
      setSavingDraft(false);
    }
  }

  if (loading) return <div className="p-6 text-slate-300">Cargando calibración de temperatura…</div>;

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-orange-300">Temperatura · {activeTemplate.shortTitle}</div>
          <h1 className="text-2xl font-semibold text-white">{activeTemplate.title}</h1>
          <p className="text-sm text-slate-300 mt-1">
            Instrumento <strong>{instrument?.tag || instrument?.lab_id || instrument?.id}</strong>
            {' · '}{instrument?.brand || '—'} {instrument?.model || ''}
            {' · Cliente: '}{instrument?.client?.name || '—'}
          </p>
        </div>
        <button
          type="button"
          className="px-3 py-2 rounded-lg border border-white/15 text-slate-200 hover:bg-white/5"
          onClick={() => navigate(`/instrument-details/${instrumentId}`)}
        >
          Volver al instrumento
        </button>
      </div>

      {notice && (
        <div className={`rounded-lg border px-4 py-3 text-sm flex justify-between gap-4 ${
          notice.type === 'success'
            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
            : 'border-rose-500/30 bg-rose-500/10 text-rose-200'
        }`}>
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)}>×</button>
        </div>
      )}

      <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4">
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-slate-900">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold">Condiciones ambientales del día</h2>
              <p className="text-xs text-slate-600">Compartidas con Presión y Eléctrica.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleSaveDailyEnvironment}
                disabled={savingDailyEnvironment}
                className="px-3 py-2 rounded border border-slate-300"
              >
                {savingDailyEnvironment ? 'Guardando…' : 'Guardar condiciones'}
              </button>
              <button
                type="button"
                onClick={handleApplyDailyEnvironment}
                disabled={!session}
                className="px-3 py-2 rounded border border-slate-300"
              >
                Aplicar a la sesión
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <NumberInput label="Temperatura (°C)" value={dailyEnvironment.temp_c} onChange={(value) => setDailyEnvironment((current) => ({ ...current, temp_c: value }))} light />
            <NumberInput label="Presión (hPa)" value={dailyEnvironment.pressure_hpa} onChange={(value) => setDailyEnvironment((current) => ({ ...current, pressure_hpa: value }))} light />
            <NumberInput label="Humedad (%HR)" value={dailyEnvironment.humidity_rh} onChange={(value) => setDailyEnvironment((current) => ({ ...current, humidity_rh: value }))} light />
          </div>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-xs text-slate-300">
            Sesión de temperatura
            <select
              value={sessionId}
              onChange={(event) => setSessionId(event.target.value)}
              className="block mt-1 min-w-[380px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900"
            >
              <option value="">Seleccionar…</option>
              {sessions.map((item) => (
                <option key={item.id} value={item.id}>
                  {resolveTemperatureTemplate(item.template_code).shortTitle} · {new Date(item.created_at).toLocaleString('es-AR')} · {item.status}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={creating}
            onClick={handleCreateSession}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium disabled:opacity-60"
          >
            {creating ? 'Creando…' : `Nueva sesión · ${resolveTemperatureTemplate(requestedTemplateCode).shortTitle}`}
          </button>
        </div>

        {session && (
          <>
            {session.review_notes && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                <strong>Observación del jefe:</strong> {session.review_notes}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <ReadField label="Estado" value={session.status} />
              <NumberInput label="LRL (°C)" value={session.instrument_range_min ?? ''} onChange={(value) => updateSessionField('instrument_range_min', value)} />
              <NumberInput label="URL (°C)" value={session.instrument_range_max ?? ''} onChange={(value) => updateSessionField('instrument_range_max', value)} />
              <NumberInput label="Resolución (°C)" value={session.instrument_resolution ?? ''} onChange={(value) => updateSessionField('instrument_resolution', value)} />
              <NumberInput label="Tolerancia absoluta (°C)" value={session.instrument_class ?? ''} onChange={(value) => updateSessionField('instrument_class', value)} />
              <label className="text-xs text-slate-300">
                Fecha de calibración
                <input
                  type="date"
                  value={session.calibration_date || todayIso()}
                  onChange={(event) => updateSessionField('calibration_date', event.target.value)}
                  className="block w-full mt-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-slate-900"
                />
              </label>
              <label className="text-xs text-slate-300">
                Validez
                <select
                  value={String(validityMonths)}
                  onChange={(event) => updateSessionField('validity_months', Number(event.target.value))}
                  className="block w-full mt-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-slate-900"
                >
                  {VALIDITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <span className="block mt-1 text-[11px] text-slate-400">Vence: {formatDate(validUntil)}</span>
              </label>
            </div>

            <TemperatureProfileFields
              template={activeTemplate}
              familyData={familyData}
              onSectionChange={updateFamilySection}
              onBudgetChange={updateBudgetField}
            />

            <label className="block text-xs text-slate-300">
              Observaciones del técnico
              <textarea
                value={session.notes || ''}
                onChange={(event) => updateSessionField('notes', event.target.value)}
                className="mt-1 min-h-[70px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900"
              />
            </label>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                disabled={savingDraft || running}
                onClick={handleSaveDraft}
                className="px-4 py-2 rounded-lg border border-slate-400/50 bg-white/5 text-white font-semibold disabled:opacity-60"
              >
                {savingDraft ? 'Guardando borrador…' : 'Guardar borrador'}
              </button>
              <button
                type="button"
                disabled={savingDraft || running || !standardValidation.valid}
                onClick={handleSaveAndCalculate}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60"
                title={!standardValidation.valid ? 'Primero validá los tres instrumentos patrones.' : undefined}
              >
                {running ? 'Guardando y calculando…' : 'Guardar todo y calcular'}
              </button>
              {!standardValidation.valid && (
                <span className="text-sm text-amber-300">
                  El cálculo se habilita cuando la trazabilidad patrón es válida.
                </span>
              )}
              {!!results.length && standardValidation.valid && (
                <span className="text-sm text-emerald-300">
                  ✓ Resultados calculados; disponibles en la bandeja del jefe.
                </span>
              )}
              {!!results.length && !standardValidation.valid && (
                <span className="text-sm text-amber-300">
                  Los resultados anteriores quedaron invalidados para aprobación hasta fijar patrones vigentes.
                </span>
              )}
            </div>
          </>
        )}
      </section>

      {session && (
        <>
          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-white">Instrumentos patrones</h2>
                <p className="text-xs text-slate-400 mt-1">
                  Solo se muestran activos verificados, con certificado vigente y alcance suficiente.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/quality/reference-standards')}
                className="px-3 py-2 rounded-lg border border-white/15 text-sm text-slate-200 hover:bg-white/5"
              >
                Abrir Instrumentos Patrones
              </button>
            </div>

            <div className={`rounded-lg border px-3 py-2 text-sm ${
              standardValidation.valid
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                : 'border-amber-500/30 bg-amber-500/10 text-amber-200'
            }`}>
              {standardValidation.valid ? (
                <span>✓ Configuración trazable válida para la fecha y el rango de esta sesión.</span>
              ) : (
                <div>
                  <div className="font-medium">Configuración pendiente</div>
                  <ul className="mt-1 list-disc pl-5 text-xs">
                    {(standardValidation.issues || []).map((issue) => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <StandardSelector
                label="1. Medio generador"
                hint={activeTemplate.profileKey === 'infrared_thermometer'
                  ? 'Cuerpo negro o fuente radiométrica certificada'
                  : activeTemplate.profileKey === 'temperature_chamber'
                    ? 'Medio generador usado para la verificación'
                    : 'Baño o bloque seco certificado'}
                value={selectedStandards.source}
                options={standardOptions.source}
                loading={loadingStandards}
                onChange={(value) => {
                  setSelectedStandards((current) => ({ ...current, source: value }));
                  setStandardValidation((current) => ({ ...current, valid: false }));
                }}
              />
              <StandardSelector
                label="2. Patrón de referencia"
                hint={activeTemplate.profileKey === 'infrared_thermometer'
                  ? 'Referencia de temperatura del cuerpo negro'
                  : 'Lector + sensor con resultados certificados'}
                value={selectedStandards.reference}
                options={standardOptions.reference}
                loading={loadingStandards}
                onChange={(value) => {
                  setSelectedStandards((current) => ({ ...current, reference: value }));
                  setStandardValidation((current) => ({ ...current, valid: false }));
                }}
              />
              <StandardSelector
                label="3. Patrón ambiental"
                hint="Temperatura, presión y humedad del laboratorio"
                value={selectedStandards.environment}
                options={standardOptions.environment}
                loading={loadingStandards}
                onChange={(value) => {
                  setSelectedStandards((current) => ({ ...current, environment: value }));
                  setStandardValidation((current) => ({ ...current, valid: false }));
                }}
              />
            </div>

            {Object.values(standardOptions).some((items) => items.length === 0) && !loadingStandards && (
              <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
                No hay una configuración completa habilitada para {formatDate(calibrationDate)} y el rango
                {' '}{session.instrument_range_min ?? '—'} a {session.instrument_range_max ?? '—'} °C.
                El jefe debe cargar y verificar los certificados vigentes en Instrumentos Patrones.
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleConfigureStandards}
                disabled={
                  configuringStandards
                  || !selectedStandards.source
                  || !selectedStandards.reference
                  || !selectedStandards.environment
                }
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-50"
              >
                {configuringStandards ? 'Validando…' : 'Validar y fijar configuración'}
              </button>
              <span className="text-xs text-slate-400">
                Cambiar la configuración invalida resultados calculados anteriormente.
              </span>
            </div>

            {standardValidation.valid && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {['source', 'reference', 'environment'].map((role) => (
                  <StandardSnapshotCard
                    key={role}
                    role={role}
                    snapshot={standardValidation.configuration?.[role]}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-white">Condiciones durante la toma de datos</h2>
                <p className="text-xs text-slate-400">Se conservan las tres lecturas de la planilla y se calcula su promedio.</p>
              </div>
              <div className="text-xs text-slate-300">
                Promedios: {fmt(averagedSession?.ambient_temp_avg, 1)} °C · {fmt(averagedSession?.ambient_pressure_avg, 1)} hPa · {fmt(averagedSession?.ambient_humidity_avg, 1)} %HR
              </div>
            </div>
            <div className="overflow-auto">
              <table className="w-full min-w-[720px] border-collapse text-sm">
                <thead>
                  <tr className="bg-white/5 text-slate-200">
                    <th className="border border-white/10 px-2 py-2 text-left">Momento</th>
                    <th className="border border-white/10 px-2 py-2">Temperatura (°C)</th>
                    <th className="border border-white/10 px-2 py-2">Presión (hPa)</th>
                    <th className="border border-white/10 px-2 py-2">Humedad (%HR)</th>
                  </tr>
                </thead>
                <tbody>
                  {familyData.environment_readings.map((reading, index) => (
                    <tr key={reading.key}>
                      <td className="border border-white/10 px-2 py-2 text-slate-300">{reading.label}</td>
                      {['temp_c', 'pressure_hpa', 'humidity_rh'].map((key) => (
                        <td key={key} className="border border-white/10 p-1">
                          <input
                            type="number"
                            step="any"
                            value={reading[key] ?? ''}
                            onChange={(event) => updateEnvironmentReading(index, key, event.target.value)}
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-slate-900"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <div>
                <h2 className="text-lg font-semibold text-white">Toma de datos · {activeTemplate.shortTitle}</h2>
              <p className="text-xs text-slate-400 mt-1">
                  Cuatro comparaciones por punto en dos series ascendentes y descendentes. Enter avanza como en Excel.
              </p>
            </div>
            <div className="overflow-auto">
              <table className="min-w-[1320px] w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-800 text-slate-100 text-center">
                    <th rowSpan={3} className="border border-white/15 px-2 py-2">Punto</th>
                    <th rowSpan={3} className="border border-white/15 px-2 py-2">Valor teórico (°C)</th>
                    <th colSpan={4} className="border border-white/15 px-2 py-2">1ª serie</th>
                    <th colSpan={4} className="border border-white/15 px-2 py-2">2ª serie</th>
                  </tr>
                  <tr className="bg-slate-800 text-slate-200 text-center">
                    <th colSpan={2} className="border border-white/15 px-2 py-1">Ascendente</th>
                    <th colSpan={2} className="border border-white/15 px-2 py-1">Descendente</th>
                    <th colSpan={2} className="border border-white/15 px-2 py-1">Ascendente</th>
                    <th colSpan={2} className="border border-white/15 px-2 py-1">Descendente</th>
                  </tr>
                  <tr className="bg-slate-800 text-slate-300 text-center">
                    {Array.from({ length: 4 }).flatMap((_, index) => [
                      <th key={`pattern-${index}`} className="border border-white/15 px-2 py-1">Patrón</th>,
                      <th key={`instrument-${index}`} className="border border-white/15 px-2 py-1">Instrumento</th>,
                    ])}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={row.point_no}>
                      <td className="border border-white/10 px-2 py-2 text-center text-slate-300">{row.point_no}</td>
                      {EDITABLE_FIELDS.map((field) => (
                        <MeasurementCell
                          key={field}
                          value={row[field]}
                          onChange={(value) => updateRow(rowIndex, field, value)}
                          onEnter={() => focusNextCell(rowIndex, field)}
                          inputRef={(element) => { inputRefs.current[`${rowIndex}-${field}`] = element; }}
                          emphasized={field === 'setpoint'}
                        />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-white">Resultados en vivo</h2>
              <p className="text-xs text-slate-400">
                Error = instrumento − patrón · U = k·uc con k=2.
              </p>
            </div>
            {liveTraceabilityIssue && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
                {liveTraceabilityIssue}
              </div>
            )}
            {!liveResults.length ? (
              <p className="text-sm text-slate-400">Completá lecturas patrón/instrumento para ver resultados.</p>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-[1180px] w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-800 text-slate-200">
                      {['Pto.', 'Media patrón', 'Media instrumento', 'Histéresis', 'Error', 'Error %', 'Repetibilidad', 'u patrón', 'u resolución', 'u temp.', 'uc', 'U (k=2)', 'Conforme'].map((label) => (
                        <th key={label} className="border border-white/15 px-2 py-2">{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {liveResults.map((result) => (
                      <tr key={result.point_no} className={result.in_tolerance === false ? 'bg-rose-500/10' : ''}>
                        <td className="border border-white/10 px-2 py-2 text-center">{result.point_no}</td>
                        <td className="border border-white/10 px-2 py-2">{fmt(result.pattern_mean)}</td>
                        <td className="border border-white/10 px-2 py-2">{fmt(result.instrument_mean)}</td>
                        <td className="border border-white/10 px-2 py-2">{fmt(result.hysteresis)}</td>
                        <td className="border border-white/10 px-2 py-2">{fmt(result.error_abs)}</td>
                        <td className="border border-white/10 px-2 py-2">{result.error_pct == null ? '—' : `${fmt(result.error_pct)} %`}</td>
                        <td className="border border-white/10 px-2 py-2">{fmt(result.repeatability)}</td>
                        <td className="border border-white/10 px-2 py-2 text-slate-400">{fmt(result.u_standard)}</td>
                        <td className="border border-white/10 px-2 py-2 text-slate-400">{fmt(result.u_resolution)}</td>
                        <td className="border border-white/10 px-2 py-2 text-slate-400">{fmt(result.u_temp)}</td>
                        <td className="border border-white/10 px-2 py-2">{fmt(result.uc)}</td>
                        <td className="border border-white/10 px-2 py-2 font-semibold">{fmt(result.u_reported)}</td>
                        <td className="border border-white/10 px-2 py-2 text-center">
                          {result.in_tolerance == null ? '—' : result.in_tolerance ? '✓' : '✕'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function TemperatureProfileFields({ template, familyData, onSectionChange, onBudgetChange }) {
  const profileKey = template?.profileKey;
  const budget = familyData?.standard_budget || {};
  let details = null;
  let budgetFields = [];

  if (profileKey === 'infrared_thermometer') {
    const infrared = familyData?.infrared || {};
    details = (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <NumberInput label="Emisividad ε" value={infrared.emissivity ?? ''} onChange={(value) => onSectionChange('infrared', 'emissivity', value)} />
        <NumberInput label="Distancia de medición (mm)" value={infrared.measurement_distance_mm ?? ''} onChange={(value) => onSectionChange('infrared', 'measurement_distance_mm', value)} />
        <NumberInput label="Diámetro del punto (mm)" value={infrared.spot_diameter_mm ?? ''} onChange={(value) => onSectionChange('infrared', 'spot_diameter_mm', value)} />
        <TextInput label="Superficie objetivo" value={infrared.target_surface ?? ''} onChange={(value) => onSectionChange('infrared', 'target_surface', value)} />
      </div>
    );
    budgetFields = [
      ['emissivity_uncertainty_c', 'Emisividad · ancho total (°C)'],
      ['distance_uncertainty_c', 'Distancia / tamaño de punto · ancho total (°C)'],
    ];
  } else if (profileKey === 'rtd_thermocouple') {
    const sensor = familyData?.sensor || {};
    details = (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SelectInput
          label="Tipo de sensor"
          value={sensor.sensor_type ?? 'PT100'}
          options={['PT100', 'PT1000', 'Termocupla J', 'Termocupla K', 'Termocupla T', 'Termocupla E', 'Termocupla N', 'Termocupla R', 'Termocupla S', 'Termocupla B', 'Otro']}
          onChange={(value) => onSectionChange('sensor', 'sensor_type', value)}
        />
        <SelectInput
          label="Conexión"
          value={sensor.connection_wires ?? '4 hilos'}
          options={['2 hilos', '3 hilos', '4 hilos', 'Conector de termocupla', 'Otra']}
          onChange={(value) => onSectionChange('sensor', 'connection_wires', value)}
        />
        <SelectInput
          label="Compensación de junta fría"
          value={sensor.cold_junction_compensation ?? 'Interna'}
          options={['Interna', 'Externa', 'No aplica']}
          onChange={(value) => onSectionChange('sensor', 'cold_junction_compensation', value)}
        />
        <NumberInput label="Profundidad de inmersión (mm)" value={sensor.immersion_depth_mm ?? ''} onChange={(value) => onSectionChange('sensor', 'immersion_depth_mm', value)} />
      </div>
    );
    budgetFields = [
      ['sensor_uncertainty_c', 'Sensor / adaptación · ancho total (°C)'],
      ['cold_junction_uncertainty_c', 'Junta fría · ancho total (°C)'],
    ];
  } else if (profileKey === 'temperature_chamber') {
    const chamber = familyData?.chamber || {};
    details = (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SelectInput label="Tipo de recinto" value={chamber.chamber_type ?? 'Estufa'} options={['Estufa', 'Cámara climática', 'Cámara de frío', 'Incubadora', 'Baño', 'Otro']} onChange={(value) => onSectionChange('chamber', 'chamber_type', value)} />
        <SelectInput label="Condición de carga" value={chamber.load_condition ?? 'Vacía'} options={['Vacía', 'Carga parcial', 'Carga nominal']} onChange={(value) => onSectionChange('chamber', 'load_condition', value)} />
        <NumberInput label="Estabilización (min)" value={chamber.stabilization_time_min ?? ''} onChange={(value) => onSectionChange('chamber', 'stabilization_time_min', value)} />
        <TextInput label="Posición de medición" value={chamber.measurement_position ?? ''} onChange={(value) => onSectionChange('chamber', 'measurement_position', value)} />
        <SelectInput label="Puerta" value={chamber.door_condition ?? 'Cerrada'} options={['Cerrada', 'Ciclo de apertura definido']} onChange={(value) => onSectionChange('chamber', 'door_condition', value)} />
      </div>
    );
    budgetFields = [
      ['stability_uncertainty_c', 'Estabilidad · ancho total (°C)'],
      ['uniformity_uncertainty_c', 'Uniformidad · ancho total (°C)'],
    ];
  }

  if (!details) return null;
  return (
    <div className="rounded-lg border border-orange-400/20 bg-orange-400/[0.05] p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-orange-100">Configuración específica</h3>
        <p className="text-xs text-slate-400 mt-1">Datos propios del método que también quedarán impresos en el certificado.</p>
      </div>
      {details}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-white/10 pt-4">
        <NumberInput label="Deriva patrón · ancho total (°C)" value={budget.drift ?? ''} onChange={(value) => onBudgetChange('drift', value)} />
        {budgetFields.map(([key, label]) => (
          <NumberInput key={key} label={label} value={budget[key] ?? ''} onChange={(value) => onBudgetChange(key, value)} />
        ))}
        <NumberInput label="Otras contribuciones · ancho total (°C)" value={budget.other_uncertainty ?? ''} onChange={(value) => onBudgetChange('other_uncertainty', value)} />
      </div>
    </div>
  );
}

function MeasurementCell({ value, onChange, onEnter, inputRef, emphasized = false }) {
  return (
    <td className={`border border-white/10 p-1 ${emphasized ? 'bg-orange-500/10' : ''}`}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onEnter();
          }
        }}
        className="w-full min-w-[94px] rounded border border-slate-300 bg-white px-2 py-1.5 text-right text-slate-900"
      />
    </td>
  );
}

function ReadField({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-300">{label}</div>
      <div className="mt-1 min-h-[34px] rounded border border-white/15 bg-black/10 px-2 py-1.5 text-sm text-slate-100">
        {value ?? '—'}
      </div>
    </div>
  );
}

function StandardSelector({ label, hint, value, options, loading, onChange }) {
  return (
    <label className="block rounded-lg border border-white/10 bg-black/10 p-3 text-xs text-slate-300">
      <span className="font-medium text-slate-100">{label}</span>
      <span className="block mt-0.5 text-[11px] text-slate-500">{hint}</span>
      <select
        value={value}
        disabled={loading}
        onChange={(event) => onChange(event.target.value)}
        className="block w-full mt-3 rounded border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 disabled:opacity-60"
      >
        <option value="">{loading ? 'Consultando…' : 'Seleccionar patrón aprobado…'}</option>
        {options.map((option) => (
          <option key={`${option.capability_id}-${option.certificate_id}`} value={option.capability_id}>
            {option.code || 'Sin código'} · {option.asset_name}
            {option.serial ? ` · S/N ${option.serial}` : ''}
            {` · Cert. ${option.certificate_no}`}
          </option>
        ))}
      </select>
      {!loading && options.length === 0 && (
        <span className="block mt-2 text-[11px] text-rose-300">Sin opciones vigentes para el alcance.</span>
      )}
    </label>
  );
}

function StandardSnapshotCard({ role, snapshot }) {
  const labels = {
    source: 'Medio generador',
    reference: 'Referencia',
    environment: 'Ambiente',
  };
  const asset = snapshot?.asset || {};
  const capability = snapshot?.capability || {};
  const certificate = snapshot?.certificate || {};
  const referenceInputMode = capability?.metadata?.reference_input_mode;
  const configurationLabel = capability?.metadata?.configuration;
  const relatedCertificates = Array.isArray(snapshot?.related_certificates)
    ? snapshot.related_certificates
    : [];
  return (
    <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] p-3 text-xs">
      <div className="font-semibold text-emerald-200">{labels[role] || role}</div>
      <div className="mt-2 text-sm text-white">
        {asset.code || 'Sin código'} · {asset.brand_model || asset.name || '—'}
      </div>
      <div className="mt-1 text-slate-300">S/N {asset.serial || '—'}</div>
      <div className="mt-1 text-slate-300">
        Alcance {capability.range_min ?? '—'} a {capability.range_max ?? '—'} {capability.unit || ''}
      </div>
      <div className="mt-1 text-slate-300">
        Cert. {certificate.certificate_no || '—'} · vence {formatDate(certificate.valid_until)}
      </div>
      {role === 'environment' && relatedCertificates.length > 1 && (
        <div className="mt-2 space-y-1 rounded border border-cyan-400/20 bg-cyan-400/[0.06] px-2 py-1.5 text-cyan-100">
          <div className="font-medium">Conjunto documental completo</div>
          {relatedCertificates.map((item) => (
            <div key={item.id || item.certificate_no} className="text-[11px] text-cyan-100/80">
              Cert. {item.certificate_no || '—'} · vence {formatDate(item.valid_until)}
            </div>
          ))}
        </div>
      )}
      {role === 'reference' && (
        <>
          {configurationLabel && (
            <div className="mt-1 text-slate-300">Configuración: {configurationLabel}</div>
          )}
          <div className="mt-1 text-slate-400">
            {(snapshot?.results || []).length} resultado(s) certificado(s) incorporado(s) al cálculo
          </div>
          <div className="mt-2 rounded border border-amber-400/20 bg-amber-400/[0.07] px-2 py-1.5 text-amber-100">
            {referenceInputMode === 'raw_indication'
              ? 'Ingresar la indicación directa: el sistema aplicará la corrección certificada.'
              : 'Ingresar la lectura del patrón ya corregida según el certificado.'}
          </div>
        </>
      )}
    </div>
  );
}

function NumberInput({ label, value, onChange, light = false }) {
  return (
    <label className={`text-xs ${light ? 'text-slate-700' : 'text-slate-300'}`}>
      {label}
      <input
        type="number"
        step="any"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="block w-full mt-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-slate-900"
      />
    </label>
  );
}

function TextInput({ label, value, onChange }) {
  return (
    <label className="text-xs text-slate-300">
      {label}
      <input
        type="text"
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="block w-full mt-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-slate-900"
      />
    </label>
  );
}

function SelectInput({ label, value, options, onChange }) {
  return (
    <label className="text-xs text-slate-300">
      {label}
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        className="block w-full mt-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-slate-900"
      >
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}
